import React from "react";
import { useSelector } from "react-redux";

import { Action, Category } from "constants/events";
import { Deal } from "custom-types/Deals";
import { Dispensary } from "custom-types/Dispensary";
import { useEventTracker } from "hooks/useEventTracker";
import useGetDealKindLabel from "hooks/useGetDealKindLabel";
import { getUserId, getUserPrefersMedical } from "redux/selectors/user";
import formatDeals from "utils/formatDeals";
import { includesDualMenuTag } from "utils/menuTypeUtils";

import DealCard from "components/botanic//DealCard";
import MenuTypeSelection from "components/Shared/MenuTypeSelection";

import NoDealsOrPromos from "../NoDealsOrPromos";

const DealSectionDealCard: React.FC<{ deal: Deal; dispensaryId: number }> = ({
  deal,
  dispensaryId,
}) => {
  const { publishEvent } = useEventTracker();
  const userId = useSelector(getUserId);
  const { getDealKindLabel } = useGetDealKindLabel();

  const sharedEventProps = {
    category: Category.dealCards,
    dealId: deal.id,
    dispensaryId: dispensaryId,
    label: getDealKindLabel(deal),
    userId,
  };

  return (
    <div className="col md:col-1/2 lg:col-1/3 mb-sm">
      <DealCard
        className="rounded h-full overflow-hidden elevation-low"
        data-testid="deal-section__deal-card"
        deal={{ ...deal, dealUrl: deal.link }}
        showMenuType
        onClick={() =>
          publishEvent({
            action: Action.click,
            ...sharedEventProps,
          })
        }
        onLoad={() =>
          publishEvent({
            action: Action.impression,
            nonInteractionEvent: true,
            ...sharedEventProps,
          })
        }
      />
    </div>
  );
};

const DealsSection: React.FC<{
  deals: Deal[];
  dispensary: Dispensary;
}> = ({ deals, dispensary }) => {
  const {
    todaysDeals,
    todaysMedDeals,
    todaysRecDeals,
    upcomingDeals,
    upcomingMedDeals,
    upcomingRecDeals,
  } = formatDeals(deals);

  const userPrefersMedical = useSelector(getUserPrefersMedical);

  const isDualMenuDispensary = includesDualMenuTag(dispensary.tags);

  const selectTodaysDeals = isDualMenuDispensary
    ? userPrefersMedical
      ? todaysMedDeals
      : todaysRecDeals
    : todaysDeals;
  const selectUpcomingDeals = isDualMenuDispensary
    ? userPrefersMedical
      ? upcomingMedDeals
      : upcomingRecDeals
    : upcomingDeals;

  const hasDeals = !!(selectTodaysDeals.length || selectUpcomingDeals.length);

  return (
    <div data-testid="deals__today-section" className="mb-xxl">
      <div className="flex items-start justify-between w-full mb-lg">
        <div>
          <h2>Current 420 deals</h2>
          {hasDeals && (
            <p className="mb-none">
              Order these limited-time deals for pickup (or delivery, where it's
              legal)!
            </p>
          )}
        </div>
        <MenuTypeSelection
          className="ml-md"
          isDualMenu={isDualMenuDispensary}
          showSelectInput={true}
        />
      </div>

      {!hasDeals && <NoDealsOrPromos type="deals" />}

      {selectTodaysDeals.length > 0 && (
        <>
          <h3 className="text-md mb-xs">Available today</h3>
          <div className="row">
            {selectTodaysDeals.map((deal) => (
              <DealSectionDealCard
                key={deal.id}
                deal={deal}
                dispensaryId={dispensary.id}
              />
            ))}
          </div>
        </>
      )}

      {selectUpcomingDeals.length > 0 && (
        <>
          <h3 className="text-green mt-xxl mb-xs">Upcoming deals</h3>
          <div className="row">
            {selectUpcomingDeals.map((deal) => (
              <DealSectionDealCard
                key={deal.id}
                deal={deal}
                dispensaryId={dispensary.id}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default DealsSection;
